import {useSelector} from 'react-redux';

import {DEVICE_TYPES} from '@/consts/device/deviceTypes';
import {selectDeviceBatteryInfo, selectDeviceBatteryVoltageFactor, selectDeviceType} from '@/state/selectors/device';

import REMAINING_STICKS_VALUES from './remainingSticksValues';

const useRemainingSticks = () => {
    const {batteryChargeUnified: batteryCharge, batteryVoltage} = useSelector(selectDeviceBatteryInfo) || {};
    const batteryVoltageFactor = useSelector(selectDeviceBatteryVoltageFactor);
    const deviceType = useSelector(selectDeviceType);

    if (!batteryCharge || !batteryVoltage || !batteryVoltageFactor) return null;

    const deviceValues = REMAINING_STICKS_VALUES[deviceType];

    if (!deviceValues) return null;

    const energy = calculateEnergy({
        deviceType,
        deviceValues,
        batteryCharge,
        batteryVoltage,
        batteryVoltageFactor,
    });

    return calculateRemainingSticks(energy, deviceValues.maxRemainingSticks, deviceType);
};

const calculateEnergy = ({deviceType, deviceValues, batteryCharge, batteryVoltage, batteryVoltageFactor}) => {
    const isNewFormula = isNewFormulaDevice(deviceType);
    const props = {
        constants: deviceValues,
        batteryCharge,
        batteryVoltage,
        batteryVoltageFactor,
    };

    return isNewFormula ? calculateNewFormulaEnergy(props) : calculateLegacyEnergy(props);
};

const NEW_FORMULA_DEVICE_TYPES = new Set([
    DEVICE_TYPES.DEV_TYPE_P1V41J_CONTROL,
    DEVICE_TYPES.DEV_TYPE_P1V41A_CONTROL,
    DEVICE_TYPES.DEV_TYPE_P1V41AU3_CONTROL,
]);

const isNewFormulaDevice = (deviceType) => NEW_FORMULA_DEVICE_TYPES.has(deviceType);

const calculateRemainingSticks = (energy, maxRemainingSticks, deviceType) => {
    const result = Math.floor(energy / REMAINING_STICKS_VALUES[deviceType].energyConstant);

    if (result < 0) return 0;
    if (result > maxRemainingSticks) return `${maxRemainingSticks}+`;

    return result;
};

const calculateNewFormulaEnergy = ({constants, batteryCharge, batteryVoltage, batteryVoltageFactor}) => {
    const {capacityConstant, voltageCoefficient, levelCoefficient} = constants;
    const normalizedVoltage = calculateNormalizedVoltage(batteryVoltage, batteryVoltageFactor);

    return normalizedVoltage * voltageCoefficient + batteryCharge * levelCoefficient + capacityConstant;
};

const calculateLegacyEnergy = ({constants, batteryCharge, batteryVoltage, batteryVoltageFactor}) => {
    const {
        capacityConstant,
        voltageThreshold,
        voltageCoefficient,
        levelThreshold,
        levelCoefficient,
        correctionCoefficient,
    } = constants;

    const normalizedVoltage = calculateNormalizedVoltage(batteryVoltage, batteryVoltageFactor) * 32;

    return (
        capacityConstant +
        (normalizedVoltage - voltageThreshold) * voltageCoefficient +
        ((batteryCharge - levelThreshold) * levelCoefficient + batteryCharge * correctionCoefficient)
    );
};

const calculateNormalizedVoltage = (batteryVoltage, batteryVoltageFactor) => {
    const voltageMultiplier = (batteryVoltageFactor + 1) * Math.pow(2, -16);

    return voltageMultiplier * batteryVoltage;
};

export default useRemainingSticks;
